import { Avatar } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { GROUP_DETAIL_LABEL } from "../group-visit-constant";
import { GroupDetail } from "../group-visit-types";
import { getInitials } from "../group-visit-utils";
import DetailCard from "./DetailCard";

export type CareTeamCardProps = {
  group: GroupDetail;
};

type CareTeamMember = { name: string; role: string; isPrimary: boolean };

const AVATAR_SIZE = 32;

/** Each member takes an equal share of the row until the card gets narrow. */
const CARE_TEAM_MEMBER_MIN_WIDTH = 200;

/**
 * @author Rajkumar Rathod
 */
const CareTeamCard = ({ group }: CareTeamCardProps) => {
  const members: CareTeamMember[] = [];

  if (group.primaryTherapistName) {
    members.push({
      name: group.primaryTherapistName,
      role: GROUP_DETAIL_LABEL.primaryTherapist,
      isPrimary: true,
    });
  }

  if (group.coTherapistName) {
    members.push({
      name: group.coTherapistName,
      role: GROUP_DETAIL_LABEL.coTherapist,
      isPrimary: false,
    });
  }

  return (
    <DetailCard title={GROUP_DETAIL_LABEL.careTeamCard}>
      <Flex gap={3} flexWrap="wrap" justifyContent="space-between">
        {members.map((member) => (
          <Flex
            key={member.role}
            alignItems="center"
            gap={1}
            sx={{ flex: 1, minWidth: CARE_TEAM_MEMBER_MIN_WIDTH }}
          >
            <Avatar
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                fontSize: CHART_DIMENSIONS.iconSm,
                bgcolor: member.isPrimary
                  ? CHART_COLORS.btnPrimaryBg
                  : CHART_COLORS.badgeDefaultBg,
                color: member.isPrimary
                  ? CHART_COLORS.surfaceWhite
                  : CHART_COLORS.textDark,
              }}
            >
              {getInitials(member.name)}
            </Avatar>

            <Flex flexDirection="column">
              <Text
                {...CHART_TYPOGRAPHY.body4}
                fontWeight={CHART_TYPOGRAPHY.weight.medium}
                c={CHART_COLORS.textDarker}
              >
                {member.name}
              </Text>
              <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
                {member.role}
              </Text>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </DetailCard>
  );
};

export default CareTeamCard;
